import {
  Alert,
  Checkbox,
  FormControlLabel,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { useFormContext } from 'react-hook-form';
import {
  EvacZone,
  FormEvacZoneStatus,
  GeoEventEvacZoneStatus,
} from 'shared/types';
import { Trans, useTranslation } from 'react-i18next';
import {
  generatePendingZoneNames,
  getPendingZoneGroups,
} from './PendingEvacZonesWarning.utils';

type PendingEvacZonesWarningProps = {
  evacZones: EvacZone[];
  zonesWithStatus: GeoEventEvacZoneStatus[];
};

type FormValues = {
  evacZoneStatuses: FormEvacZoneStatus[];
  releasePendingZoneUids?: string[];
};

export const PendingEvacZonesWarning = (
  props: PendingEvacZonesWarningProps,
): JSX.Element | null => {
  const { evacZones, zonesWithStatus } = props;
  const { t } = useTranslation();
  const { watch, setValue } = useFormContext<FormValues>();

  const releasePendingZoneUids = watch('releasePendingZoneUids', [])!;

  const pendingZoneGroups = getPendingZoneGroups(
    evacZones,
    zonesWithStatus.map((eZS) => eZS.evacZone.uidV2),
  );

  const hasPendingZoneGroups = Boolean(pendingZoneGroups.length);

  const handleChange = (
    action: 'add' | 'remove',
    pendingZoneUids: string[],
  ): void => {
    if (action === 'add') {
      setValue('releasePendingZoneUids', [
        ...releasePendingZoneUids,
        ...pendingZoneUids,
      ]);
      return;
    }

    setValue(
      'releasePendingZoneUids',
      releasePendingZoneUids.filter((uid) => !pendingZoneUids.includes(uid)),
    );
  };

  const releasePendingZoneUidsString = releasePendingZoneUids.join(',');

  if (!hasPendingZoneGroups) {
    return null;
  }

  return (
    <Grid item xs={12}>
      <Alert severity="warning" data-testid="pending-evac-zones-warning">
        <Stack spacing={2}>
          <Typography>{t('pendingEvacZonesWarning.title')}</Typography>

          {pendingZoneGroups.map((zoneGroup) => {
            const key = zoneGroup.releaseUids.join(',');
            const { activeNames, resultingNames } = generatePendingZoneNames(
              t,
              zoneGroup,
            );
            const checked = releasePendingZoneUidsString.includes(key);
            return (
              <Stack key={key} spacing={1} gap={1}>
                <Typography>
                  <Trans
                    i18nKey="pendingEvacZonesWarning.message"
                    values={{ activeNames, resultingNames }}
                  >
                    <Typography component="span" fontWeight="fontWeightBold">
                      {activeNames}
                    </Typography>
                    will be replaced by
                    <Typography component="span" fontWeight="fontWeightBold">
                      {resultingNames}
                    </Typography>
                  </Trans>
                </Typography>

                <FormControlLabel
                  control={
                    <Checkbox
                      name="acceptPendingReview"
                      checked={checked}
                      onChange={() =>
                        handleChange(
                          checked ? 'remove' : 'add',
                          zoneGroup.releaseUids,
                        )
                      }
                      id="acceptPendingReview"
                    />
                  }
                  label={t('pendingEvacZonesWarning.accept')}
                  sx={{
                    '.MuiFormControlLabel-label': {
                      fontWeight: 'fontWeightBold',
                    },
                  }}
                />
              </Stack>
            );
          })}
        </Stack>
      </Alert>
    </Grid>
  );
};
